import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Paper,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Switch,
  Fade
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import axios from 'axios';
import useUserProfile from '../hooks/useUserProfile';
import SupplementsTable from './SupplementsTable';
import config from '../config';
import '../common.css';
import { useTheme } from '@mui/material/styles';
import { color } from '../theme/tokens';
import { calculateAge } from '../Utils';
import EditIcon from '@mui/icons-material/Edit';
import { buttonStyles } from './common/ButtonStyles';
import StackComposer from './common/StackComposer';
import AgentActivityPanel from './common/AgentActivityPanel';
import { fetchConditions, fetchSupplements } from '../Utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { saveOrUpdateStack } from '../firebase/stackActions';
import { useFooter } from '../contexts/FooterContext';
import { logAnalyticsEvent } from '../utils/analytics';
import { useStackGeneration } from '../contexts/StackGenerationContext';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { extractMedicationIngredients } from '../api/medications';
import { checkInteractionsForComponent, extractSupplementKeys } from '../utils/interactionChecker';
import { 
  groupInteractionsBySupplementKey, 
  createEnhancedInteractionData 
} from '../utils/interactionUtils';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useBannerQueue } from '../hooks/useBannerQueue';
import { BannerDisplay } from './common/BannerDisplay';
import { BANNER_TYPES, createBanner } from '../config/banners';
import { AutoFixHighIcon } from '../icons';
import { RequiredMark, nameFieldErrorProps } from './analyze/AnalyzeFormParts';
import { useAuth } from '../AuthContext';
import {
  getGuestMedications,
  recallCheckedMedications,
  rememberCheckedMedications,
} from '../utils/guestMedications';
import { buildStackSnapshot, generationRequestBody } from '../utils/stackSnapshot';

const { BASE_URL, MAX_REQUESTS } = config;
const MIN_LOADING_DISPLAY_TIME = 3000; // 3 seconds (NEW)
// Extra time after a job completes for AgentActivityPanel to finish playing
// back queued stages/events before results replace it (fast-drain pace).
// Full grace for fast runs (whole transcript arrives in 1-2 polls); short
// tail for long runs whose playback has mostly kept up.
const COMPLETION_PLAYBACK_GRACE_MS = 2500;
const COMPLETION_PLAYBACK_TAIL_MS = 1000;

const getEntryContextFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const healthGoals = params.get('healthGoals') || '';
  const autoGenerate = params.get('auto_generate') === 'true';

  if (!healthGoals) {
    return null;
  }

  return {
    healthGoals,
    autoGenerate,
  };
};

// Define messages and their approximate durations (summing to ~17 seconds)
const loadingStages = [
  { message: "Analyzing your health goals...", duration: 2000, progressTarget: 12 }, // 2s
  { message: "Retrieving research studies...", duration: 2000, progressTarget: 29 }, // 3s
  { message: "Evaluating supplement effectiveness...", duration: 4000, progressTarget: 53 }, // 4s
  { message: "Gathering supplement details...", duration: 1000, progressTarget: 71 }, // 3s
  { message: "Compiling relevant research studies...", duration: 1000, progressTarget: 88 }, // 3s
  { message: "Finalizing your supplement stack...", duration: 4000, progressTarget: 95 }, // 2s -> 95% (stays here if longer)
];
const TOTAL_ANIMATION_TIME = loadingStages.reduce((sum, stage) => sum + stage.duration, 0); // ~17000ms

function CreateStack() {
  const [stackName, setStackName] = useState('');
  // Save Stack was disabled while the name was empty, with nothing at the
  // field saying why. It now stays pressable and, pressed without a name,
  // marks the field and puts the cursor there (2026-10-04).
  const [nameMissing, setNameMissing] = useState(false);
  const nameInputRef = useRef(null);
  const [healthGoals, setHealthGoals] = useState('');
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showStackDetails, setShowStackDetails] = useState(false);
  const [citationsData, setCitationsData] = useState(null);
  const [pendingDrawerRestoreInfo, setPendingDrawerRestoreInfo] = useState(null);
  // A logged-out visitor's medication check (STACK-565) needs the exact goal
  // string sent to /v2/generate-stack (the server finds the stack by it), the
  // supplement keys the generator produced (anything added later is "Not
  // checked"), and the medications from the tab store as they were when the
  // stack arrived — the stack page's medications are read-only.
  const [generatedGoals, setGeneratedGoals] = useState('');
  const [generatedKeys, setGeneratedKeys] = useState([]);
  const [checkedMedications, setCheckedMedications] = useState([]);
  const authState = useAuth();
  const { userProfile } = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const {
    jobId,
    pendingJobId,
    healthGoal: pendingHealthGoal,
    status: jobStatus,
    result: jobResult,
    pollCount,
    error: jobError,
    // Live backend progress (renamed: `progress` is taken by the local
    // fallback animation percentage below).
    progress: liveProgress,
    isInProgress,
    startJob,
    beginGenerating,
    clearJob,
    kind: jobKind,
  } = useStackGeneration();
  const [isInitialized, setIsInitialized] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [supplements, setSupplements] = useState([]);
  const [conditions, setConditions] = useState([]);
  const { hasAccess, isPro } = useFeatureAccess();
  const [entryContext, setEntryContext] = useState(null);
  const entryContextRef = useRef(null);
  const autoGenerateStartedRef = useRef(false);
  
  // Banner queue for managing multiple banners with priority
  const { addBanner, removeBanner, activeBanner } = useBannerQueue();

  // --- Loading State ---
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(loadingStages[0]?.message || "Loading..."); // Start with first message
  const [currentStageIndex, setCurrentStageIndex] = useState(0); // Track current stage index
  const generationStartTimeRef = useRef(null); // NEW Ref
  const stageTimeoutRef = useRef(null);
  const stackDetailsRef = useRef(null);
  const loginBannerAnchorRef = useRef(null);
  const [showLoginBanner, setShowLoginBanner] = useState(false);

  // Add a new state variable for notes (around line 47 with other state variables)
  const [notes, setNotes] = useState('');

  // Polling now lives in StackGenerationProvider; this page consumes provider state
  // (jobStatus / jobResult / pollCount). displayedJobRef guards against rendering the
  // same completed result more than once per job.
  const displayedJobRef = useRef(null);
  // Synchronous guard against a rapid double-click firing two generations before
  // React flushes loadingAI (see handleGenerateStack).
  const submitInFlightRef = useRef(false);

  // Add state for interaction handling
  const [showInteractionWarning, setShowInteractionWarning] = useState(false);
  const [showInteractionConfirmDialog, setShowInteractionConfirmDialog] = useState(false);
  const [highRiskSupplements, setHighRiskSupplements] = useState([]);
  const [mediumRiskSupplements, setMediumRiskSupplements] = useState([]);
  const [lowRiskSupplements, setLowRiskSupplements] = useState([]);

  // Add state for interaction checking
  const [isCheckingInteractions, setIsCheckingInteractions] = useState(false);
  const [interactionCheckComplete, setInteractionCheckComplete] = useState(false);
  const [interactionCheckError, setInteractionCheckError] = useState(null);

  // Helper function to fetch user medications from Firestore
  const fetchUserMedications = async (userId) => {
    try {
      const medProfileRef = doc(db, 'med_profile', userId);
      const medProfileDoc = await getDoc(medProfileRef);
      
      if (medProfileDoc.exists()) {
        const medProfileData = medProfileDoc.data();
        return medProfileData.medications || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching user medications:', error);
      return [];
    }
  };

  // Helper function to extract supplement keys from generated stack



  // Phase F: Helper function to get user-friendly medication name using in_rxcui correlation
  const getUserFriendlyMedicationName = (medicationInRxcui, medications = null) => {
    // Use provided medications or fetch from the medications we have in context
    const medsToSearch = medications || [];
    
    console.log('[CreateStack] getUserFriendlyMedicationName DEBUG:', {
      medicationInRxcui,
      medicationType: typeof medicationInRxcui,
      medicationsCount: medsToSearch.length,
      firstMedication: medsToSearch[0],
      allMedInRxcuis: medsToSearch.map(med => ({
        display: med.name?.display,
        in_rxcui: med.identifiers?.in_rxcui,
        rxcui: med.identifiers?.rxcui
      }))
    });
    
    if (!medicationInRxcui || !medsToSearch.length) {
      console.log('[CreateStack] Early return: no medicationInRxcui or empty medications array');
      return 'Unknown medication';
    }

    // Direct lookup: find the medication with matching in_rxcui
    const matchingMed = medsToSearch.find(med => {
      const medInRxcui = med.identifiers?.in_rxcui;
      const match = medInRxcui && medInRxcui.toString() === medicationInRxcui.toString();
      console.log('[CreateStack] Checking medication:', {
        medDisplay: med.name?.display,
        medInRxcui,
        searchingFor: medicationInRxcui,
        match
      });
      return match;
    });

    if (matchingMed) {
      console.log('[CreateStack] Found matching medication:', matchingMed.name?.display);
      return matchingMed.name?.display || 'Unknown medication';
    }

    console.log('[CreateStack] No matching medication found for in_rxcui:', medicationInRxcui);
    return 'Unknown medication';
  };

  // Interaction checking is now handled by SupplementsTable component

  // Function to check for interaction warnings
  const checkForInteractionWarnings = (supplements) => {
    if (!supplements || !Array.isArray(supplements)) {
      setHighRiskSupplements([]);
      setMediumRiskSupplements([]);
      setLowRiskSupplements([]);
      setShowInteractionWarning(false);
      return { hasWarnings: false, highRisk: [], mediumRisk: [], lowRisk: [] };
    }

    // Check for interactions (handles both new stacks and old saved stacks without interaction data)
    // Use case-insensitive comparison for risk levels
    const highRisk = supplements.filter(s => 
      s?.interaction?.has_interaction && s.interaction.risk?.toLowerCase() === 'high'
    );
    const mediumRisk = supplements.filter(s => 
      s?.interaction?.has_interaction && s.interaction.risk?.toLowerCase() === 'medium'
    );
    const lowRisk = supplements.filter(s => 
      s?.interaction?.has_interaction && s.interaction.risk?.toLowerCase() === 'low'
    );
    
    setHighRiskSupplements(highRisk);
    setMediumRiskSupplements(mediumRisk);
    setLowRiskSupplements(lowRisk);
    
    const hasWarnings = highRisk.length > 0 || mediumRisk.length > 0 || lowRisk.length > 0;
    setShowInteractionWarning(hasWarnings);
    
    return { hasWarnings, highRisk, mediumRisk, lowRisk };
  };


  // ===== JOB CANCELLATION =====

  // Handle cancel function. Polling lives in the provider, so cancelling is just
  // clearing the job; the provider's poll loop stops when status leaves 'pending'.
  const handleCancel = () => {
    if (pendingHealthGoal) {
      setHealthGoals(pendingHealthGoal);
    }
    clearJob();
    setLoadingAI(false);
    setErrorMessage('');
    generationStartTimeRef.current = null;
    displayedJobRef.current = null;
  };

  // ===== CONSUME PROVIDER JOB STATE =====
  //
  // The provider runs the poll loop app-wide and persists status + result to
  // sessionStorage, so a job that finishes while the user is on another page is
  // captured. This effect reacts to those status transitions and renders the
  // result — which is why returning to this page after completion now shows the
  // finished stack instead of the empty form.
  useEffect(() => {
    // One app-wide job slot is shared with the Analyze Current Stack flow
    // (`/manual-create-stack`), whose result has a different shape. Ignoring a
    // foreign job here is what stops an in-flight analysis from being read as
    // a generated stack if the member navigates to this page while it runs.
    // See `kind` in StackGenerationContext.
    if (jobKind !== 'generate') {
      return undefined;
    }

    if (jobStatus === 'starting' || jobStatus === 'pending') {
      // Kicking off (pre-job_id) or resuming a pending job — show the loader and
      // mirror the goal.
      displayedJobRef.current = null;
      setErrorMessage('');
      setLoadingAI(true);
      if (pendingHealthGoal && !healthGoals) {
        setHealthGoals(pendingHealthGoal);
      }
      if (!generationStartTimeRef.current) {
        generationStartTimeRef.current = Date.now();
      }
      return undefined;
    }

    if (jobStatus === 'completed' && jobResult) {
      // Only render a given job's result once per mount-cycle.
      if (displayedJobRef.current === jobId) {
        return undefined;
      }
      displayedJobRef.current = jobId;

      const responseData = jobResult;
      const isValid =
        responseData &&
        typeof responseData === 'object' &&
        (responseData.supplements || responseData.name || responseData.stackName);

      if (!isValid) {
        console.error('Completed job has no valid result data');
        setErrorMessage('Stack completed but the result was in an unexpected format. Please try again.');
        setLoadingAI(false);
        clearJob();
        return undefined;
      }

      const goalForDisplay = pendingHealthGoal || healthGoals;
      // Honor the minimum loading display only when we actually showed the loader
      // this visit. A returning user whose job already finished sees it instantly.
      const startedAt = generationStartTimeRef.current;
      // Fast runs deliver their whole activity transcript in 1-2 polls; give
      // the panel's playback a short grace to finish the story (it switches
      // to fast-drain once `complete`) instead of cutting to results mid-line.
      // Long runs have mostly drained already and only need a small tail.
      const elapsed = startedAt ? Date.now() - startedAt : 0;
      const playbackGrace = startedAt && liveProgress
        ? (elapsed < 15000 ? COMPLETION_PLAYBACK_GRACE_MS : COMPLETION_PLAYBACK_TAIL_MS)
        : 0;
      const delayNeeded = startedAt
        ? Math.max(MIN_LOADING_DISPLAY_TIME - elapsed, playbackGrace)
        : 0;

      const finish = () => {
        displayGeneratedStack(responseData, goalForDisplay);
        generationStartTimeRef.current = null;
        clearJob();
      };

      if (delayNeeded > 0) {
        const timer = setTimeout(finish, delayNeeded);
        return () => clearTimeout(timer);
      }
      finish();
      return undefined;
    }

    if (jobStatus === 'failed' || jobStatus === 'expired') {
      if (pendingHealthGoal && !healthGoals) {
        setHealthGoals(pendingHealthGoal);
      }
      setErrorMessage(jobError || 'Stack generation failed. Please try again.');
      setLoadingAI(false);
      generationStartTimeRef.current = null;
      clearJob();
      return undefined;
    }

    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus, jobResult, jobId, jobKind]);

  // Define getStackId at the top level of the component
  const getStackId = () => {
    const params = new URLSearchParams(window.location.search);
    let stackId = params.get('tempId');
    
    // If no tempId in URL, check sessionStorage
    if (!stackId) {
      stackId = sessionStorage.getItem('currentStackTempId');
      
      // If we found an ID in sessionStorage, update the URL
      if (stackId) {
        params.set('tempId', stackId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
      }
    }
    
    // If still no tempId, generate a new one
    if (!stackId) {
      stackId = 'temp_' + Date.now();
      params.set('tempId', stackId);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Always keep the current tempId in sessionStorage
    sessionStorage.setItem('currentStackTempId', stackId);
    return stackId;
  };

  // Add initialization effect
  useEffect(() => {
    const initializeStack = async () => {
      const currentUrl = window.location.href;
      const urlParams = new URL(currentUrl);
      const source = urlParams.searchParams.get('source');
      const shouldGenerate = sessionStorage.getItem('shouldGenerateStack');
      const pendingGoals = sessionStorage.getItem('pendingHealthGoals');
      const urlEntryContext = getEntryContextFromSearch(location.search);
      
      // Restore scroll position
      const savedPosition = sessionStorage.getItem('stackScrollPosition');
      if (savedPosition) {
        window.scrollTo(0, parseInt(savedPosition));
        sessionStorage.removeItem('stackScrollPosition');
      }

      if (
        !isInitialized &&
        urlEntryContext?.healthGoals &&
        urlEntryContext.autoGenerate &&
        !autoGenerateStartedRef.current
      ) {
        autoGenerateStartedRef.current = true;
        entryContextRef.current = urlEntryContext;
        setEntryContext(urlEntryContext);
        setHealthGoals(urlEntryContext.healthGoals);
        setIsInitialized(true);
        await new Promise(resolve => setTimeout(resolve, 0));
        await handleGenerateStack(urlEntryContext.healthGoals);
        return;
      }

      if (urlEntryContext && !entryContext) {
        entryContextRef.current = urlEntryContext;
        setEntryContext(urlEntryContext);
      }

      if (!isInitialized && source === 'landing' && shouldGenerate === 'true' && pendingGoals) {
        setHealthGoals(pendingGoals);
        setIsInitialized(true);
        
        sessionStorage.removeItem('pendingHealthGoals');
        sessionStorage.removeItem('shouldGenerateStack');
        
        await new Promise(resolve => setTimeout(resolve, 0));
        await handleGenerateStack(pendingGoals);
      }
    };

    initializeStack();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, location.search]);

  // Pro users automatically get enhanced AI intelligence - no toggle needed

  // Effect to restore health goals when component mounts
  useEffect(() => {
    // Only run once when component mounts and if health goals are empty
    if (!healthGoals) {
      const urlEntryContext = getEntryContextFromSearch(location.search);
      if (urlEntryContext?.healthGoals) {
        entryContextRef.current = urlEntryContext;
        setEntryContext(urlEntryContext);
        setHealthGoals(urlEntryContext.healthGoals);
        return;
      }

      // First priority: prefillGoal from navigation state (e.g., from curated stacks)
      if (location.state?.prefillGoal) {
        setHealthGoals(location.state.prefillGoal);
        return;
      }
      
      // Second priority: restore from pending context
      if (pendingHealthGoal) {
        setHealthGoals(pendingHealthGoal);
        return;
      }
      
      // Third priority: try localStorage
      const lastUsedGoalsData = localStorage.getItem('lastUsedHealthGoals');
      if (lastUsedGoalsData) {
        try {
          const parsed = JSON.parse(lastUsedGoalsData);
          const oneDay = 24 * 60 * 60 * 1000; // 24 hours
          
          // Only restore if less than 24 hours old
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < oneDay) {
            setHealthGoals(parsed.goals);
          } else {
            // Clean up old data
            localStorage.removeItem('lastUsedHealthGoals');
          }
        } catch (error) {
          // Handle legacy format or corrupted data
          setHealthGoals(lastUsedGoalsData);
        }
      }
    }
  }, []); // Empty dependency array - only run on mount

  // ===== ANIMATION LOGIC (POLL-COUNT BASED) =====

  // This useEffect handles the animation timing based on poll count.
  useEffect(() => {
    if (loadingAI) {
      // Calculate which stage we should be at based on poll count
      // We poll every 3 seconds, so with pollsPerStage = 1, each stage advances every 3 seconds
      // This makes the animation feel more responsive and engaging
      const pollsPerStage = 1; // Advance to next stage every poll (every 3 seconds)
      const targetStageIndex = Math.min(
        Math.floor(pollCount / pollsPerStage),
        (loadingStages?.length || 1) - 1
      );
      
      // Update stage and message if we've advanced to a new stage
      if (targetStageIndex !== currentStageIndex && loadingStages?.[targetStageIndex]) {
        setCurrentStageIndex(targetStageIndex);
        setCurrentMessage(loadingStages[targetStageIndex]?.message || "Loading...");
        
        // Smoothly animate progress to the new target over 500ms
        const targetProgress = loadingStages[targetStageIndex]?.progressTarget || 50;
        const currentProgress = progress;
        const progressDiff = targetProgress - currentProgress;
        const animationDuration = 500; // 500ms
        const steps = 20; // 20 steps for smooth animation
        const stepSize = progressDiff / steps;
        const stepInterval = animationDuration / steps;
        
        let step = 0;
        const progressInterval = setInterval(() => {
          step++;
          const newProgress = currentProgress + (stepSize * step);
          
          if (step >= steps) {
            setProgress(targetProgress); // Ensure we end exactly at target
            clearInterval(progressInterval);
          } else {
            setProgress(newProgress);
          }
        }, stepInterval);
      }
    } else {
      // Reset progress when not loading
      setProgress(0);
              setCurrentMessage(loadingStages[0]?.message || "Loading...");
      setCurrentStageIndex(0);
    }
  }, [loadingAI, pollCount, currentStageIndex, progress]);

  const sampleGoals = [
    'Weight Management',
    'Muscle Health',
    'Digestion ',
    'Energy ',
    'Sleep Hygiene',
    'Energy ',
    'Heart Health',
    'Focus',
    'Stress Relief ',
    'Immunity',
    'Healthy Hair & Skin ',
    'Healthy Aging ',
    'Memory ',
    'Joint Health',
    'Bone Health ',
    'Hormone Balance ',
    'Prostate Health ',
    'Eye Health',
    'Prenatal & Postnatal',
    'Healthy Mood',
    'Gluclose Metabolism '
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!stackName || !stackName.trim()) {
      setNameMissing(true);
      nameInputRef.current?.focus();
      nameInputRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }

    // Check if interaction check is still in progress
    if (isCheckingInteractions) {
      setShowInteractionConfirmDialog(true);
      return;
    }

    // Check for any risk interactions before saving
    const { hasWarnings, highRisk, mediumRisk, lowRisk } = checkForInteractionWarnings(selectedSupplements);
    
    // Show confirmation dialog for any level of interaction risk
    if (hasWarnings && (highRisk.length > 0 || mediumRisk.length > 0 || lowRisk.length > 0)) {
      setShowInteractionConfirmDialog(true);
      return;
    }

    await saveStack();
  };

  // Separate function to handle the actual saving
  const saveStack = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      try {
        // Prepare the core stack data (without refs/timestamps)
        // IMPORTANT: Strip out interaction data - it should never be saved to Firestore
        // Also filter only selected supplements
        const supplementsWithoutInteractions = selectedSupplements
          .filter(s => s.selected !== false)
          .map(supplement => {
            const { interaction, selected, ...supplementWithoutInteraction } = supplement;
            return supplementWithoutInteraction;
          });

        // Validate that at least one supplement is included
        if (supplementsWithoutInteractions.length === 0) {
          setErrorMessage('Please include at least one supplement in your stack.');
          return;
        }

        const stackCoreData = {
          name: stackName,
          supplements: supplementsWithoutInteractions,
          citations: citationsData || null,
          origin: 'AI', // Origin for stacks created via this component
          version: config.STACK_VERSION,
          // userId will be added internally by saveOrUpdateStack
        };

        // Call the centralized function to save the new stack,
        // passing the content for goals and notes directly.
        await saveOrUpdateStack(
          stackCoreData,
          healthGoals, // Pass the goals string directly
          notes,       // Pass the notes string directly
          null         // Pass null for stackId to indicate creation
        );

        // Log the analytics event
        logAnalyticsEvent('save_stack', { 
          origin: 'AI',
          number_of_supplements: selectedSupplements.length,
          health_goals: healthGoals,
          supplement_ids: selectedSupplements.map(s => s.key)
        });

        // Clear localStorage after successful submission
        const params = new URLSearchParams(window.location.search);
        const tempId = params.get('tempId');
        if (tempId) {
          localStorage.removeItem(`unsavedStack_${tempId}`);
          sessionStorage.removeItem('currentStackTempId');
        }

        navigate('/dashboard');
      } catch (error) {
        console.error("[CreateStack handleSubmit] Error:", error);
        
        let errorMessage = 'Unable to save your stack. Please try again.';
        
        if (error.code === 'permission-denied') {
          errorMessage = 'You don\'t have permission to save this stack. Please sign in again.';
        } else if (error.code === 'unavailable') {
          errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
        } else if (error.message?.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
        
        setErrorMessage(errorMessage);
      }
    } else {
      setErrorMessage('');
      navigate('/login');
    }
  };

  const [showRequestLimitModal, setShowRequestLimitModal] = useState(false);

  // --- Display Generated Stack Function --- (NEW)
  const displayGeneratedStack = useCallback((responseData, goalsUsedForGeneration) => {
    // Add validation and error handling
    if (!responseData) {
      console.error('displayGeneratedStack: responseData is null or undefined');
      setErrorMessage('Invalid response data received');
      setLoadingAI(false);
      return;
    }


    
    // Preserve health goals in form state for future use
    if (goalsUsedForGeneration && !healthGoals) {
      setHealthGoals(goalsUsedForGeneration);
    }
    
    // Save health goals to localStorage for persistence across navigation
    if (goalsUsedForGeneration) {
      const healthGoalsData = {
        goals: goalsUsedForGeneration,
        timestamp: Date.now()
      };
      localStorage.setItem('lastUsedHealthGoals', JSON.stringify(healthGoalsData));
    }
    
    setStackName(responseData.name || responseData.stackName || 'Generated Stack');
    setSelectedSupplements(responseData.supplements || []);
    setGeneratedGoals(goalsUsedForGeneration || '');
    setGeneratedKeys(
      (responseData.supplements || []).map((s) => s?.supplementId).filter(Boolean)
    );
    setCheckedMedications(getAuth().currentUser ? [] : getGuestMedications());
    setCitationsData(responseData.citations || null);
    setShowStackDetails(true);
    setLoadingAI(false);

    // Reset interaction check state
    setIsCheckingInteractions(false);
    setInteractionCheckComplete(false);
    setInteractionCheckError(null);

    // Check for interaction warnings from existing data (for compatibility)
    if (responseData.supplements) {
      checkForInteractionWarnings(responseData.supplements);
    }

    const auth = getAuth();
    const user = auth.currentUser;

    // Interaction checking is now handled by SupplementsTable component
    // when supplements are set via setSelectedSupplements

    const recommendedSupplementsData = responseData.supplements || [];
    const currentCitationsData = responseData.citations || {};
    const supplementScores = recommendedSupplementsData.reduce((acc, s) => {
      if (s.supplementId && s.evidenceScore !== undefined) {
        acc[s.supplementId] = s.evidenceScore;
      }
      return acc;
    }, {});
    const citedArticleIds = [
      ...new Set(
        Object.values(currentCitationsData)
              .flat()
              .map(citation => citation.article_id)
              .filter(Boolean)
      )
    ];
    logAnalyticsEvent('stack_generation_success', {
        health_goals: goalsUsedForGeneration,
        recommended_supplement_ids: recommendedSupplementsData.map(s => s.supplementId) || [],
        recommended_supplement_scores: supplementScores, 
        cited_article_ids: citedArticleIds, 
        citation_count: citedArticleIds.length, 
        trigger_context: entryContextRef.current?.autoGenerate ? 'url_auto_generate' : 'create_stack_page'
    });

    setTimeout(() => {
      if (user) {
        stackDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        loginBannerAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    if (!user) {
       let currentRequestCount = parseInt(localStorage.getItem('stackRequestCount')) || 0;
       currentRequestCount++;
       localStorage.setItem('stackRequestCount', currentRequestCount);
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.get('tempId')) {
        const tempId = 'temp_' + Date.now();
        params.set('tempId', tempId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
    }
  }, [navigate, entryContext]); // Removed healthGoals from here, as it's now passed as an arg

  const handleGenerateStack = async (passedGoals = null) => {
    // Prevent overlapping generations. submitInFlightRef is a *synchronous* guard:
    // loadingAI is React state and won't have flushed between two clicks landing in
    // the same tick, so a rapid double-click would otherwise fire two POSTs.
    // isInProgress (starting | pending) blocks a second generation even across a
    // remount, where the synchronous ref would have reset.
    if (isInProgress || loadingAI || submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;

    // Fresh generation — allow the next completed result to render.
    displayedJobRef.current = null;

    generationStartTimeRef.current = Date.now();
    setLoadingAI(true);
    
    let requestCount = parseInt(localStorage.getItem('stackRequestCount')) || 0;
    setStackName('');
    setSelectedSupplements([]);
    setGeneratedGoals('');
    setGeneratedKeys([]);
    setCheckedMedications([]);
    setCitationsData(null);
    setShowStackDetails(false);
    setErrorMessage('');

    const goalsToLogAndSend = passedGoals || healthGoals;
    const triggerContext = entryContextRef.current?.autoGenerate
      ? 'url_auto_generate'
      : passedGoals
        ? 'landing_page'
        : 'create_stack_page';
    
    // Preserve health goals in form state when starting generation
    if (goalsToLogAndSend && !healthGoals) {
      setHealthGoals(goalsToLogAndSend);
    }
    
    logAnalyticsEvent('generate_stack', {
      origin: 'AI',
      health_goals: goalsToLogAndSend,
      enhanced_ai: isPro, // Track if user has Pro enhanced AI
      trigger_context: triggerContext
    });

    // No scroll-on-start. This used to jump to an inline loading block that
    // was the only progress indicator; StackGenerationContext now owns live
    // progress, so scrolling away from the composer to an empty spacer just
    // yanks the page. The completion handler still scrolls to the result.

    const auth = getAuth();
    const user = auth.currentUser;
    let token = '';
    let calculatedAge = 'unknown';
    let gender = 'unknown';
  
    try {
      if (user && userProfile) { 
        token = await user.getIdToken(); 
        calculatedAge = calculateAge(userProfile.dateOfBirth);
        gender = (userProfile.gender === 'other') ? 'neutral' : (userProfile.gender || 'unknown');
      } else if (!user) { 
        if (requestCount >= MAX_REQUESTS) {
          setLoadingAI(false);
          setShowRequestLimitModal(true);
          logAnalyticsEvent('stack_generation_limit_reached', {
            request_limit: MAX_REQUESTS,
            trigger_context: triggerContext
          });
          return;
        }
        setShowLoginBanner(true);
      }

      // Record 'starting' now — before the (possibly slow, cold-start) POST returns
      // a job_id — so the in-progress indicator shows immediately and the state
      // survives navigating away during the wait. startJob() promotes it to pending.
      beginGenerating(goalsToLogAndSend);

      const endpoint = `${BASE_URL}/v2/generate-stack`;
      
      // Never medications: see generationRequestBody.
      const response = await axios.post(endpoint, generationRequestBody(
        goalsToLogAndSend,
        calculatedAge,
        gender
      ), {
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });

      // Handle async response (202) or synchronous response (200)
      if (response.status === 202) {
        // Async job started - extract job id and start polling
        const { job_id } = response.data;


        if (!job_id) {
          console.error('No job_id received in async response');
          setErrorMessage('No job key received from server');
          setLoadingAI(false);
          clearJob(); // clear the 'starting' marker
          return;
        }

        // Hand the job to the provider, which owns the app-wide poll loop. This
        // page reacts to the resulting status transitions (see the consume effect).
        startJob(job_id, goalsToLogAndSend);
      } else {
        // Synchronous response (backward compatibility) — clear the 'starting'
        // marker; this path displays the result directly without a provider job.
        clearJob();
        const responseData = response.data;
        const elapsedTime = Date.now() - generationStartTimeRef.current;
        const delayNeeded = MIN_LOADING_DISPLAY_TIME - elapsedTime;

        if (delayNeeded > 0) {
          setTimeout(() => {
            displayGeneratedStack(responseData, goalsToLogAndSend);
          }, delayNeeded);
        } else {
          displayGeneratedStack(responseData, goalsToLogAndSend);
        }
      }
  
    } catch (error) {
       console.error("Error generating stack:", error);
       
       let errorMessage = 'Unable to generate your stack. Please try again.';
       
       // Check for specific API errors
       if (error.response?.status === 429) {
         errorMessage = 'You\'ve reached your daily limit. Subscribe for unlimited stacks.';
       } else if (error.response?.status === 503) {
         errorMessage = 'Our AI service is temporarily busy. Please try again in a moment.';
       } else if (error.response?.status >= 500) {
         errorMessage = 'Server error. Our team has been notified. Please try again later.';
       } else if (error.message?.includes('network') || error.code === 'ERR_NETWORK') {
         errorMessage = 'Network error. Please check your connection and try again.';
       } else if (error.response?.data?.error && !error.response.data.error.includes('Error')) {
         // Use API error message if it's user-friendly (doesn't contain "Error")
         errorMessage = error.response.data.error;
       }
       
       setErrorMessage(errorMessage);
       setLoadingAI(false);
       clearJob(); // clear the 'starting' marker on failure
    } finally {
      // Release the synchronous submit guard. By the time the POST resolves, a 202
      // has set pendingJobId (which guards re-entry) and the same-tick double-click
      // window is long closed.
      submitInFlightRef.current = false;
    }
  };

  const toggleRowExpansion = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const toggleEditMode = (index) => {
    setEditMode(editMode === index ? null : index);
    if (editMode !== index) {
      setExpandedRow(index);
    }
  };

  const handleSampleGoalClick = (goal) => {
    const currentGoals = healthGoals ? healthGoals.split(',').map(g => g.trim()) : [];
    if (!currentGoals.includes(goal)) {
      const newGoals = [...currentGoals, goal].join(', ');
      setHealthGoals(newGoals);
    }
  };
 

  // Effect to check for returning stack state (MODIFIED for drawer)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tempId = params.get('tempId');

    if (tempId) {
      const savedStackString = localStorage.getItem(`unsavedStack_${tempId}`);
      if (savedStackString) {
        try {
          const savedStack = JSON.parse(savedStackString);
          const oneHour = 60 * 60 * 1000;

          if (new Date().getTime() - (savedStack.timestamp || 0) < oneHour) {
            setSelectedSupplements(savedStack.supplements || []);
            setStackName(savedStack.stackName || '');
            setHealthGoals(savedStack.healthGoals || '');
            setGeneratedGoals(savedStack.generatedGoals || '');
            setGeneratedKeys(Array.isArray(savedStack.generatedKeys) ? savedStack.generatedKeys : []);
            // The list this stack was checked against, not the composer's
            // current one: medications are fixed per stack (PR 390 review).
            setCheckedMedications(getAuth().currentUser ? [] : (recallCheckedMedications(tempId) || []));
            setCitationsData(savedStack.citations || null);

            // Restore DRAWER state info if present (Look for drawerState)
            if (savedStack.drawerState && savedStack.drawerState.isOpen) {
               setPendingDrawerRestoreInfo({ // <-- Set state for SupplementsTable drawer
                  supplementId: savedStack.drawerState.supplementId,
                  scroll: savedStack.drawerState.scroll
               });
            } else {
               setPendingDrawerRestoreInfo(null); // Ensure it's null if not restoring drawer
            }

            setShowStackDetails(true); // Show details after restoring

            // Restore scroll position (unchanged)
            const mainScroll = localStorage.getItem(`unsavedStack_${tempId}_mainScroll`);
            if (mainScroll) {
               setSavedScrollPosition(parseInt(mainScroll));
               localStorage.removeItem(`unsavedStack_${tempId}_mainScroll`);
            }

            // Clear the main state item AFTER successful restoration
            localStorage.removeItem(`unsavedStack_${tempId}`);

          } else {
            localStorage.removeItem(`unsavedStack_${tempId}`);
            localStorage.removeItem(`unsavedStack_${tempId}_mainScroll`);
          }
        } catch (error) {
          localStorage.removeItem(`unsavedStack_${tempId}`);
          localStorage.removeItem(`unsavedStack_${tempId}_mainScroll`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Function to save state before navigating (MODIFIED for drawer)
  const saveStateAndNavigate = (targetUrl, drawerInfo = null) => {
    const stackId = getStackId();
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

    // Main stack state. A logged-out visitor's snapshot keeps no interaction
    // results (buildStackSnapshot); members' is unchanged.
    const stackState = buildStackSnapshot({
      supplements: selectedSupplements,
      stackName,
      healthGoals,
      citations: citationsData,
      drawerInfo,
      generatedGoals,
      generatedKeys,
      loggedIn: Boolean(getAuth().currentUser),
    });

    localStorage.setItem(`unsavedStack_${stackId}`, JSON.stringify(stackState));
    // The medications stay out of that localStorage snapshot; the list this
    // stack was checked against goes to the tab store instead. Always, even
    // when empty: a later generation on this page keeps the same tempId, and
    // an earlier stack's list must not be restored onto it.
    if (!getAuth().currentUser) {
      rememberCheckedMedications(stackId, checkedMedications);
    }

    // Save main scroll position separately
    localStorage.setItem(`unsavedStack_${stackId}_mainScroll`, currentScroll.toString());

    // Navigate
    window.location.href = targetUrl;
  };

  // Handler for main supplement link click (passed to SupplementsTable) - unchanged logic, just uses saveState
  const handleMainSupplementLinkClick = (supplementId, e) => {
    e.preventDefault();
    if (!supplementId) return;
    const stackId = getStackId();
    const targetUrl = `/supplements/${supplementId}?tempId=${stackId}`;
    // Save state WITHOUT drawer info
    saveStateAndNavigate(targetUrl, null);
  };

  // Handler for article link click INSIDE the DRAWER (passed to SupplementsTable)
  const handleDrawerArticleLinkClickWrapper = (articleId, currentSupplementId, drawerScroll) => {
     if (!articleId || !currentSupplementId) return;
     const stackId = getStackId();
     const targetUrl = `/supplements/${currentSupplementId}/research/${articleId}?tempId=${stackId}`;
     const drawerInfo = { // Use drawerInfo
        supplementId: currentSupplementId,
        scroll: drawerScroll
     };
     // Save state WITH drawer info
     saveStateAndNavigate(targetUrl, drawerInfo);
  };

  // Add effect to load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [supplementsList, conditionsList] = await Promise.all([
          fetchSupplements(config),
          fetchConditions(config)
        ]);
        
        setSupplements(supplementsList);
        setConditions(conditionsList);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  const { setShowFooter } = useFooter();
  
  // Set up scroll listener
  useEffect(() => {
    const handleScroll = () => {
      // Show footer when scrolled more than 62.5rem (adjust this value as needed)
      const scrollThreshold = 1000;
      const shouldShowFooter = window.scrollY > scrollThreshold;
      setShowFooter(shouldShowFooter);
    };
    
    // Hide footer initially when component mounts
    setShowFooter(false);
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
      setShowFooter(true); // Restore footer visibility when leaving page
    };
  }, [setShowFooter]);

  // Set only for a logged-out visitor who added medications before this stack
  // was generated, with the feature on. Members never get one.
  const guestSignedOut = Boolean(authState) && !authState.loading && !authState.currentUser;
  const guestCheck = useMemo(() => (
    config.FEATURES.GUEST_MEDICATION_CHECK && guestSignedOut
      && checkedMedications.length > 0 && generatedGoals && generatedKeys.length > 0
      ? { healthGoals: generatedGoals, medications: checkedMedications, generatedKeys }
      : null
  ), [guestSignedOut, checkedMedications, generatedGoals, generatedKeys]);

  return (
    <Container component="main" maxWidth="md">
      {/* Banner display - shows highest priority banner */}
      <BannerDisplay 
        banner={activeBanner} 
        onDismiss={removeBanner} 
      />
      
      {/* Center the composer + loading panel together. A flex parent is used
          because a global `.MuiBox-root { margin: 0 !important }` rule defeats
          mx:'auto' self-centering on any Box. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box
        sx={{
          pt: { xs: 3, sm: 4, md: 5 },
          pb: 2,
          width: '100%',
          maxWidth: '40rem',
        }}
      >
        <StackComposer
          healthGoals={healthGoals}
          setHealthGoals={setHealthGoals}
          onSubmit={handleGenerateStack}
          onCancel={handleCancel}
          // Treat a pending job as busy too (not just loadingAI), so the user
          // can't resubmit and spawn an overlapping job during a poll/reconnect
          // window or an error path that clears loadingAI before the job ends.
          loading={loadingAI || Boolean(pendingJobId)}
          surface="app"
          showHeader={false}
        />
      </Box>

           {/* Anchor for login banner scrolling */}
           <Box ref={loginBannerAnchorRef} sx={{ height: 0, width: '100%', mb: 0 }}></Box>

      {/* --- Loading Placeholder Section --- */}
      <Box sx={{ minHeight: loadingAI ? '6.25rem' : '0', mt: 3, width: '100%', maxWidth: '40rem', transition: 'min-height 0.3s ease-out' }}>
        {loadingAI && (
          /* ── Live agent activity panel (real progress when the backend
                reports it; falls back to the rotated copy otherwise) ── */
          <AgentActivityPanel
            progress={liveProgress}
            // Pre-202 window (job not yet created — no server contact has
            // completed) shows the same label the backend seeds into the job
            // row, so click → 202 → first real stage reads as one stream.
            fallbackLabel={jobStatus === 'starting' ? 'Starting research analysis' : currentMessage}
            fallbackPct={progress}
            resetKey={jobId || 'starting'}
            complete={jobStatus === 'completed'}
            footer={pendingJobId && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    borderColor: 'rgba(27, 79, 114, 0.4)',
                    color: '#1B4F72',
                    fontWeight: 600,
                    padding: '0.5rem 1.5rem',
                    borderRadius: '0.75rem',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      borderColor: '#1B4F72',
                      backgroundColor: 'rgba(27, 79, 114, 0.06)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          >
              {entryContext?.healthGoals && (
                <Chip
                  icon={<AutoFixHighIcon />}
                  label={`Building stack for ${healthGoals || entryContext.healthGoals}`}
                  sx={{
                    mb: 1,
                    maxWidth: '100%',
                    height: 'auto',
                    py: 0.5,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(27, 79, 114, 0.08)',
                    color: '#1B4F72',
                    border: '0.0625rem solid rgba(27, 79, 114, 0.2)',
                    '& .MuiChip-label': { whiteSpace: 'normal', fontWeight: 600, lineHeight: 1.3 },
                    '& .MuiChip-icon': { color: '#1B4F72' },
                  }}
                />
              )}

              <Typography
                sx={{
                  display: 'block',
                  mb: 1.5,
                  fontSize: '0.8rem',
                  color: '#8A9BAD',
                }}
              >
                While we build your science-based stack, feel free to explore the app.
              </Typography>

            </AgentActivityPanel>
        )}
      </Box>
      </Box>



      {/* Login Banner */}
      {!loadingAI && showLoginBanner && !getAuth().currentUser && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2.5,
            borderRadius: '1rem',
            background: 'rgba(158, 179, 132, 0.1)',
            border: '0.0625rem solid #9EB384',
            boxShadow: '0 0.125rem 0.75rem rgba(0,0,0,0.05)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#435334', mb: 0.5 }}>
                Unlock Your Personalized Stacks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your first two stacks are based on general research. Start your free 14-day trial to unlock unlimited personalized stacks.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                // Was iOS blue. The brief locks one action colour with dark
                // ink on the fill; white on the accent fails AA at body size.
                background: color.accent,
                color: color.accentInk,
                fontWeight: 600,
                px: 2,
                py: 1,
                borderRadius: '0.5rem',
                textTransform: 'none',
                transition: '200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                '&:hover': {
                  background: color.accentHover,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              Start Free Trial
            </Button>
          </Box>
        </Paper>
      )}

      {/* Stack Results Section (Now appears below placeholder area) */}
      <Fade in={showStackDetails && !loadingAI} timeout={500} key={showStackDetails ? 'details-visible' : 'details-hidden'}>
        {/* Ref is on the inner Box for final scroll adjustment */}
        <Box sx={{ mt: 0 }}>
          <Box ref={stackDetailsRef}>
            {/* Stack Name Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: '1rem',
                background: '#fff',
                boxShadow: '0 0.125rem 0.75rem rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'  // Allows wrapping on mobile
              }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ minWidth: 'fit-content' }}
                >
                  Stack Name<RequiredMark />:
                </Typography>
                <TextField
                  value={stackName}
                  onChange={(e) => {
                    setStackName(e.target.value);
                    if (e.target.value.trim()) setNameMissing(false);
                  }}
                  inputRef={nameInputRef}
                  {...nameFieldErrorProps(nameMissing)}
                  variant="standard"
                  fullWidth
                  sx={{
                    maxWidth: { xs: '100%', sm: '25rem' },
                    '& .MuiInput-root': {
                      fontSize: '1rem',
                      fontWeight: 400,
                      '&:before': {
                        borderBottom: '0.125rem solid #E2E8F0'
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: '0.125rem solid #CBD5E0'
                      },
                      '&.Mui-focused:after': {
                        borderBottom: '0.125rem solid #4299E1'
                      }
                    },
                    '& .MuiInput-input': {
                      padding: '0.25rem 0'
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            const input = document.querySelector('input[value="' + stackName + '"]');
                            if (input) {
                              input.focus();
                              input.select();
                            }
                          }}
                          sx={{ 
                            color: '#A0AEC0',
                            padding: '0.25rem',
                            '&:hover': {
                              color: '#4A5568'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            </Paper>


            {/* Subtle Safety Notice for Interaction Check Failure */}
            {interactionCheckError && (
              <Alert
                severity="warning"
                variant="outlined"
                icon={<InfoOutlinedIcon />}
                sx={{
                  mb: 2,
                  borderRadius: '0.75rem',
                  backgroundColor: '#fffbf0',
                  borderColor: '#fbbf24',
                  color: '#92400e',
                  boxShadow: '0 1px 4px rgba(251, 191, 36, 0.12)',
                  py: 1,
                  '& .MuiAlert-icon': {
                    color: '#f59e0b',
                    fontSize: '18px'
                  },
                  '& .MuiAlert-message': {
                    fontSize: '13px',
                    lineHeight: 1.4,
                    py: 0
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25, color: '#92400e', fontSize: '13px' }}>
                      Interaction check temporarily unavailable
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#92400e', lineHeight: 1.3, fontSize: '12px' }}>
                      We couldn't verify interactions with your medications. As always, please consult your healthcare provider before starting these supplements.
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    backgroundColor: '#fef3c7', 
                    borderRadius: '6px', 
                    px: 1, 
                    py: 0.25,
                    minWidth: 'fit-content'
                  }}>
                    <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600, fontSize: '11px' }}>
                      Safety First
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}

            <SupplementsTable
              selectedSupplements={selectedSupplements}
              setSelectedSupplements={setSelectedSupplements}
              guestCheck={guestCheck}
              citations={citationsData}
              getStackId={getStackId}
              handleMainLinkClick={handleMainSupplementLinkClick}
              handleDrawerLinkClick={handleDrawerArticleLinkClickWrapper}
              pendingDrawerRestoreInfo={pendingDrawerRestoreInfo}
              onDrawerRestored={() => setPendingDrawerRestoreInfo(null)}
              stackVersion={config.STACK_VERSION}
            />
            
            {/* Notes Section.
                The notes card holds the notes field and nothing else. Cancel
                and Save used to sit inside it, which read as "save this note"
                rather than "save this stack" — they are the page's actions,
                so they belong outside the card they were nested in. */}
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: '1rem',
                background: '#fff',
                boxShadow: '0 0.125rem 0.75rem rgba(0,0,0,0.08)',
              }}
            >
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add notes about your stack (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                    backgroundColor: '#F7FAFC',
                    '&:hover': {
                      backgroundColor: '#EDF2F7',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#F7FAFC',
                      boxShadow: '0 0 0 0.125rem rgba(66, 153, 225, 0.2)',
                    }
                  }
                }}
              />
            </Paper>

            {/* Error message display */}
            {errorMessage && (
              <Alert
                severity="error"
                sx={{ mt: 2, borderRadius: '0.5rem' }}
                onClose={() => setErrorMessage('')}
              >
                {errorMessage}
              </Alert>
            )}

            <Box sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                sx={buttonStyles.secondary}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={selectedSupplements.length === 0 || selectedSupplements.filter(s => s.selected !== false).length === 0}
                sx={{
                  // Was the legacy olive `#435334` with white text. The design
                  // system locks one action colour, and white on it fails AA at
                  // body size — hence `accentInk` rather than white.
                  backgroundColor: color.accent,
                  color: color.accentInk,
                  fontWeight: 600,
                  borderRadius: '0.625rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: color.accentHover,
                    opacity: 1,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 0.25rem 0.75rem rgba(10, 156, 95, 0.25)'
                  },
                  '&:disabled': {
                    backgroundColor: color.line,
                    color: color.inkMuted,
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>{/* End of ref Box */}
        </Box>
      </Fade>

      {/* Add space after the stack details to push footer down */}
      {showStackDetails && !loadingAI && (
        <Box sx={{ height: { xs: '3.75rem', sm: '5rem', md: '6.25rem' }, width: '100%' }} />
      )}



      {/* Interaction Confirmation Dialog */}
      <Dialog 
        open={showInteractionConfirmDialog} 
        onClose={() => setShowInteractionConfirmDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            maxWidth: '35rem',
            mx: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          color: '#5f6368',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <InfoOutlinedIcon />
          {isCheckingInteractions ? 'Interaction Check In Progress' : 'Review Potential Interactions'}
        </DialogTitle>
        <DialogContent>
          {isCheckingInteractions ? (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We're still checking for potential interactions between your medications and supplements.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, backgroundColor: 'rgba(33, 150, 243, 0.08)', borderRadius: '0.5rem' }}>
                <CircularProgress size={24} />
                <Typography variant="body2">
                  This usually takes 30-60 seconds...
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Would you like to save your stack now, or wait for the interaction check to complete?
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Some supplements in your stack may interact with your medications:
              </Typography>
          
          {highRiskSupplements.length > 0 && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(245, 124, 0, 0.08)', borderRadius: '0.5rem' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#f57c00' }}>
                Higher Risk Interactions:
              </Typography>
              {highRiskSupplements.map((supplement, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  • <strong>{supplement.name || 'Unknown supplement'}:</strong> {supplement.interaction?.reasoning || 'No details available'}
                </Typography>
              ))}
            </Box>
          )}
          
          {mediumRiskSupplements.length > 0 && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255, 160, 0, 0.08)', borderRadius: '0.5rem' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ffa000' }}>
                Medium Risk Interactions:
              </Typography>
              {mediumRiskSupplements.map((supplement, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  • <strong>{supplement.name || 'Unknown supplement'}:</strong> {supplement.interaction?.reasoning || 'No details available'}
                </Typography>
              ))}
            </Box>
          )}

          {lowRiskSupplements.length > 0 && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(158, 179, 132, 0.08)', borderRadius: '0.5rem' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#6B7280' }}>
                Lower Risk Interactions:
              </Typography>
              {lowRiskSupplements.map((supplement, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  • <strong>{supplement.name || 'Unknown supplement'}:</strong> {supplement.interaction?.reasoning || 'No details available'}
                </Typography>
              ))}
            </Box>
          )}
          
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {highRiskSupplements.length > 0 || mediumRiskSupplements.length > 0 
                  ? 'Consider discussing these supplements with your healthcare provider before use. Would you like to save this stack?'
                  : 'These interactions are generally low risk, but we recommend discussing with your healthcare provider. Would you like to save this stack?'
                }
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button 
            onClick={() => setShowInteractionConfirmDialog(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            {isCheckingInteractions ? 'Wait for Check' : 'Cancel'}
          </Button>
          <Button 
            onClick={async () => {
              setShowInteractionConfirmDialog(false);
              await saveStack();
            }}
            variant="contained"
            color="primary"
            sx={{ fontWeight: 600 }}
          >
            Save Stack{isCheckingInteractions ? ' Anyway' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={showRequestLimitModal} 
        onClose={() => setShowRequestLimitModal(false)}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            maxWidth: '25rem',
            mx: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          color: '#435334',
          fontWeight: 600 
        }}>
          You are on a roll!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Start your 14-day free trial for unlimited personalized stacks. Cancel anytime.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button 
            onClick={() => {
              setShowRequestLimitModal(false);
              navigate('/login');
            }} 
            variant="contained"
            sx={{
              // Was the deprecated pre-brief green. Aligned with the banner CTA
              // above so the same action is never two different greens
              // depending on where you meet it.
              background: color.accent,
              color: color.accentInk,
              fontWeight: 600,
              px: 2,
              py: 1,
              borderRadius: '0.5rem',
              textTransform: 'none',
              transition: '200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                background: color.accentHover,
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            Start Free Trial
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default CreateStack;
